import {
  cacheReceivedV2DepthField,
  createLocalModelDepthField,
  readCachedV2DepthField
} from "../../../shared/particles/particlePhotoLocalDepth";
import { readLocalMediaOriginalUrl } from "../../../shared/platform/localOriginalMedia";
import { probeRuntime, type RuntimeProbe } from "../../../shared/platform/runtime";
import { publishV2DepthField } from "./v2DepthRelayRepository";

export type V2DepthJobStatus = "queued" | "generating" | "ready" | "waiting" | "fallback";

interface V2DepthJob {
  galaxyId: string;
  mediaId: string;
  runtime?: RuntimeProbe;
  visibleImageUrl?: string;
}

interface V2DepthJobEvent {
  mediaId: string;
  status: V2DepthJobStatus;
}

export const V2_DEPTH_JOB_EVENT = "memory-universe:v2-depth-status";
export const V2_DEPTH_READY_EVENT = "memory-universe:v2-depth-ready";

const queuedJobs = new Map<string, V2DepthJob>();
const runningJobs = new Set<string>();
let drainPromise: Promise<void> | null = null;

function emit(mediaId: string, status: V2DepthJobStatus) {
  window.dispatchEvent(new CustomEvent<V2DepthJobEvent>(V2_DEPTH_JOB_EVENT, { detail: { mediaId, status } }));
}

export function canGenerateV2DepthHere(runtime: RuntimeProbe) {
  // Browser tabs are allowed on desktop. Android runs only in the installed
  // client, where its local original is durable and the task can be resumed
  // whenever the application is active again.
  if (runtime.kind === "tauri") return runtime.platform === "windows" || runtime.platform === "android";
  return typeof window !== "undefined" && window.innerWidth > 800;
}

async function loadVisibleImage(source: string, revokeSource: boolean) {
  const image = new Image();
  if (!source.startsWith("blob:")) image.crossOrigin = "anonymous";
  image.decoding = "async";
  image.src = source;
  try {
    await image.decode();
    return { image, source: revokeSource ? source : null };
  } catch {
    if (revokeSource) URL.revokeObjectURL(source);
    return null;
  }
}

async function loadLocalImage(mediaId: string, visibleImageUrl?: string) {
  const source = await readLocalMediaOriginalUrl(mediaId);
  if (source) return loadVisibleImage(source, true);
  // Older memories may predate the device-original relay but still have an
  // authorized preview already visible in the current page. It is sufficient
  // for the 256px derived V2 field and never leaves this device as pixels.
  return visibleImageUrl ? loadVisibleImage(visibleImageUrl, false) : null;
}

async function processJob(job: V2DepthJob) {
  const runtime = job.runtime ?? await probeRuntime();
  if (!canGenerateV2DepthHere(runtime)) {
    emit(job.mediaId, "waiting");
    return;
  }

  const cached = await readCachedV2DepthField(job.mediaId);
  if (cached) {
    // A previous local result can still be the first authorized device to
    // publish the encrypted derivative for everyone else.
    try {
      await publishV2DepthField(job.galaxyId, job.mediaId, cached, runtime);
    } catch {
      // The local V2 field remains valid even when the relay retries later.
    }
    emit(job.mediaId, "ready");
    window.dispatchEvent(new CustomEvent<string>(V2_DEPTH_READY_EVENT, { detail: job.mediaId }));
    return;
  }

  const local = await loadLocalImage(job.mediaId, job.visibleImageUrl);
  if (!local) {
    // This device has not received a durable original yet. Another authorized
    // device may generate the same derivative first.
    emit(job.mediaId, "waiting");
    return;
  }

  emit(job.mediaId, "generating");
  try {
    const aspectRatio = Math.max(0.01, local.image.naturalWidth / Math.max(1, local.image.naturalHeight));
    const field = await createLocalModelDepthField(local.image, aspectRatio);
    if (!field) {
      emit(job.mediaId, "fallback");
      return;
    }
    await cacheReceivedV2DepthField(job.mediaId, field);
    window.dispatchEvent(new CustomEvent<string>(V2_DEPTH_READY_EVENT, { detail: job.mediaId }));
    emit(job.mediaId, "ready");
    try {
      await publishV2DepthField(job.galaxyId, job.mediaId, field, runtime);
    } catch {
      // The encrypted relay is retried by this and other authorized devices.
    }
  } finally {
    if (local.source) URL.revokeObjectURL(local.source);
  }
}

async function drain() {
  while (queuedJobs.size > 0) {
    const [mediaId, job] = queuedJobs.entries().next().value as [string, V2DepthJob];
    queuedJobs.delete(mediaId);
    if (runningJobs.has(mediaId)) continue;
    runningJobs.add(mediaId);
    try {
      await processJob(job);
    } catch {
      emit(mediaId, "fallback");
    } finally {
      runningJobs.delete(mediaId);
    }
  }
}

function ensureDrain() {
  if (drainPromise) return;
  drainPromise = drain().finally(() => {
    drainPromise = null;
    // A task may arrive after drain() observes an empty Map. Start a new
    // serialized pass directly; routing through schedule would reject it as
    // already queued and leave it stranded until a later app session.
    if (queuedJobs.size > 0) ensureDrain();
  });
}

/**
 * Queue an authorized local V2 calculation. Work is deliberately serialized:
 * depth inference is memory-heavy and parallel model loads are harmful on a
 * phone. A later authorized device can safely enqueue the same media id.
 */
export function scheduleV2DepthGeneration(job: V2DepthJob) {
  if (!job.galaxyId || !job.mediaId || runningJobs.has(job.mediaId) || queuedJobs.has(job.mediaId)) return;
  queuedJobs.set(job.mediaId, job);
  emit(job.mediaId, "queued");
  ensureDrain();
}
